/* ============================================================
   US-Parts24 — interactions (loaded once, deferred)
   - Scroll reveals (safe: never hides already-visible content,
     skipped in theme editor + reduced-motion → text never invisible)
   - Cursor-follow glow on dark surfaces
   - NHTSA vPIC VIN decoder (free, no key) with graceful fallback
   ============================================================ */
(function () {
  'use strict';

  var inEditor = window.Shopify && window.Shopify.designMode;
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var finePointer = window.matchMedia && window.matchMedia('(pointer: fine)').matches;

  /* ---------- 1. Scroll reveals ---------------------------- */
  function initReveals() {
    if (inEditor || reduceMotion || !('IntersectionObserver' in window)) return;

    /* R515 — die GRUPPE einblenden, nicht jedes Kind.
     *
     * Marcel am 22.09.2026: „diese Zoom-Dings und so, das könnte alles noch
     * ein bisschen besser werden. Diese Scroll-Transitions …"
     *
     * Gemessen auf der Startseite: **36 Elemente** blendeten einzeln ein —
     * allein 12 Kategoriekacheln, 8 Fragen und 4 Kennzahlen. Jede mit eigener
     * Verzögerung, jede mit eigener GPU-Ebene. Das ist kein Effekt mehr,
     * das ist Grundrauschen: Beim Scrollen bewegt sich die halbe Seite
     * dauernd, ohne dass die Bewegung etwas bedeutet.
     *
     * Ein Raster ist EIN Gedanke und blendet als EINER ein. Damit werden
     * aus 24 Einzelbewegungen 5 — und der gestaffelte Auftritt (--usp-rd)
     * hat weiter seinen Platz dort, wo wenige Elemente stehen.
     */
    var selector = [
      '.usp-section-head',
      '.usp-cats', '.usp-faq', '.usp-stats__grid',   /* Gruppen statt .usp-cat / .usp-faq__item / .usp-stat */
      '.usp-benefit', '.usp-how__step', '.usp-brand',
      /* Die Kategoriekacheln tragen BEIDE Klassen (`usp-cat usp-card--link`).
         Ohne den Ausschluss faengt `.usp-card` sie wieder einzeln ein —
         gemessen: 13 → 14 statt 13 → 1. */
      '.usp-card:not(.usp-cat)', '.usp-text__body',
      '.usp-reqcta', '.usp-contact', '.usp-fitment__head', '.usp-fitment__details',
      '.usp-acc__item',
      '.usp-hero__copy > *', '.usp-hero__box'
    ].join(',');

    var vh = window.innerHeight || document.documentElement.clientHeight;
    var nodes = Array.prototype.slice.call(document.querySelectorAll(selector));
    var parentCounters = new WeakMap();

    /* R485: Nach dem Einblenden die Reveal-Klassen wieder ABNEHMEN.
       Gemessen am 17.09. auf der Startseite: 55 Elemente trugen dauerhaft
       `will-change: opacity, transform` — 49 davon ausserhalb des Bildes.
       `.usp-reveal` setzt diesen Hinweis, und die Klasse blieb für immer am
       Element kleben (der Beobachter meldet sich ab, räumt aber nicht auf).
       Jedes dieser Elemente hält damit dauerhaft eine eigene GPU-Ebene —
       genau das, was sich als „laggt leicht" anfühlt.
       Nach dem Übergang sind beide Klassen überflüssig: der Ruhezustand
       (Deckkraft 1, kein Transform) IST der Normalzustand des Elements. */
    function aufraeumen(el) {
      el.classList.remove('usp-reveal', 'usp-in');
      el.style.removeProperty('--usp-rd');
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        el.classList.add('usp-in');
        io.unobserve(el);
        /* Der Übergang dauert 0,7 s plus bis zu 0,35 s Verzögerung.
           Der Wecker ist die Rückfallebene: Läuft gar kein Übergang (z. B.
           weil das Element schon am Ziel steht), feuert transitionend nie. */
        var fertig = false;
        var erledigen = function () {
          if (fertig) return;
          fertig = true;
          aufraeumen(el);
        };
        el.addEventListener('transitionend', erledigen, { once: true });
        setTimeout(erledigen, 1400);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });

    nodes.forEach(function (el) {
      var rect = el.getBoundingClientRect();
      // Already visible on load → leave untouched (no flash, never hidden)
      if (rect.top < vh * 0.92) return;
      var parent = el.parentElement;
      var idx = parentCounters.get(parent) || 0;
      parentCounters.set(parent, idx + 1);
      el.style.setProperty('--usp-rd', Math.min(idx * 70, 350) + 'ms');
      el.classList.add('usp-reveal');
      io.observe(el);
    });
  }

  /* ---------- 1b. One-shot Diagnose-Scan (.usp-scan) -------
     Rote Scan-Linie fährt EINMAL, sobald das Element in den Viewport
     scrollt. Kein Verstecken von Inhalt → nie unsichtbar. Editor +
     reduced-motion bekommen nichts. */
  function initScans() {
    if (inEditor || reduceMotion || !('IntersectionObserver' in window)) return;
    var nodes = document.querySelectorAll('.usp-scan');
    if (!nodes.length) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-scanned');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.25 });
    Array.prototype.forEach.call(nodes, function (el) { io.observe(el); });
  }

  /* ---------- 1b. Boot-Sequenz (R20) ------------------------
     [data-usp-boot] (How-it-works-Schritte): beim Reinscrollen .is-live
     setzen → Status-LEDs füllen sich nacheinander grün (CSS-Delays).
     reduced-motion/Editor/alte Browser: sofort .is-live (statisch grün). */
  function initBootSeq() {
    var els = document.querySelectorAll('[data-usp-boot]');
    if (!els.length) return;
    if (inEditor || reduceMotion || !('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(els, function (el) { el.classList.add('is-live'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-live');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.35 });
    Array.prototype.forEach.call(els, function (el) { io.observe(el); });
  }

  /* ---------- 2. Cursor-follow glow ------------------------ */
  function initGlow() {
    if (!finePointer) return;
    var targets = document.querySelectorAll('[data-usp-glow]');
    if (!targets.length) return;

    targets.forEach(function (el) {
      var raf = null, lastX = 0, lastY = 0;
      el.addEventListener('pointermove', function (e) {
        var rect = el.getBoundingClientRect();
        lastX = e.clientX - rect.left;
        lastY = e.clientY - rect.top;
        if (raf) return;
        raf = requestAnimationFrame(function () {
          el.style.setProperty('--usp-mx', lastX + 'px');
          el.style.setProperty('--usp-my', lastY + 'px');
          el.classList.add('usp-glow-on');
          raf = null;
        });
      });
      el.addEventListener('pointerleave', function () {
        el.classList.remove('usp-glow-on');
      });
    });
  }

  /* ---------- 3. VIN decoder (NHTSA vPIC) ------------------ */
  var VIN_RE = /^[A-HJ-NPR-Z0-9]{17}$/i; // valid VIN chars (no I,O,Q)
  var vinCache = {};

  function titleCase(s) {
    if (!s) return '';
    return s.toLowerCase().replace(/\b([a-z])/g, function (m, c) { return c.toUpperCase(); });
  }

  function buildEngine(r) {
    var parts = [];
    if (r.DisplacementL) parts.push(parseFloat(r.DisplacementL).toFixed(1).replace('.0', '') + ' L');
    if (r.EngineCylinders) parts.push(r.EngineCylinders + '-Zyl.');
    if (r.FuelTypePrimary && /gasoline|diesel|electric|hybrid|flex/i.test(r.FuelTypePrimary)) {
      parts.push(r.FuelTypePrimary.replace('Gasoline', 'Benzin').replace('Diesel', 'Diesel'));
    }
    return parts.join(' · ');
  }

  var FUEL_DE = { 'Gasoline': 'Benzin', 'Diesel': 'Diesel', 'Electric': 'Elektro', 'Flexible Fuel Vehicle (FFV)': 'Flex-Fuel' };

  // vPIC liefert den Antrieb sperrig ("4WD/4-Wheel Drive/4x4") → knappe DE-Form.
  function shortDrive(s) {
    if (!s) return '';
    var t = s.toLowerCase();
    if (t.indexOf('4wd') >= 0 || t.indexOf('4x4') >= 0 || t.indexOf('4-wheel') >= 0 || t.indexOf('four') >= 0) return '4×4';
    if (t.indexOf('awd') >= 0 || t.indexOf('all-wheel') >= 0 || t.indexOf('all wheel') >= 0) return 'Allrad';
    if (t.indexOf('rwd') >= 0 || t.indexOf('rear') >= 0) return 'Heckantrieb';
    if (t.indexOf('fwd') >= 0 || t.indexOf('front') >= 0) return 'Frontantrieb';
    return s;
  }

  /* R540 · Sichtbare Felder: automatisch Eingetragenes darf die NÄCHSTE VIN
     überschreiben (vorher blieb nach einer VIN-Korrektur das alte Fahrzeug
     stehen). Von Hand Geändertes bleibt unangetastet. */
  function setField(scope, key, value) {
    var el = scope.querySelector('[data-usp-fill="' + key + '"]');
    if (!el || !value) return;
    if (!el.value || el.value === el.dataset.uspAuto) {
      el.value = value; el.dataset.uspAuto = value;
      el.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /* R540 · Alles, was eine frühere VIN eingetragen hat, wieder leeren.
     Befund 22.09.: Charger erkannt → VIN auf eine andere, unvollständige
     geändert → die versteckten Felder schickten weiter „Dodge Charger (2017)"
     samt Motor und Werk mit. Marcel hätte eine Anfrage mit falschem Fahrzeug
     bekommen. Aufgerufen in JEDEM Zweig ohne neues Ergebnis. */
  function clearVinData(scope) {
    if (!scope || !scope.querySelectorAll) return;
    Array.prototype.forEach.call(scope.querySelectorAll('[data-usp-detail]'), function (el) {
      if (el.type === 'hidden' || el.value === el.dataset.uspAuto) el.value = '';
      delete el.dataset.uspAuto;
    });
    Array.prototype.forEach.call(scope.querySelectorAll('[data-usp-fill]'), function (el) {
      if (el.dataset.uspAuto && el.value === el.dataset.uspAuto) { el.value = ''; }
      delete el.dataset.uspAuto;
    });
    scope.__uspVeh = null;
    var save = scope.querySelector('.usp-garage-save'); if (save) save.remove();
  }

  /* R540 · VIN-Prüfziffer (Stelle 9). Für Fahrzeuge aus Nordamerika (VIN
     beginnt mit 1–5) ist sie Pflicht — eine falsche Prüfziffer heißt fast
     immer: vertippt. Das ist eine echte Prüfung, keine Show. */
  var VIN_WERT = { A: 1, B: 2, C: 3, D: 4, E: 5, F: 6, G: 7, H: 8, J: 1, K: 2, L: 3, M: 4, N: 5, P: 7, R: 9, S: 2, T: 3, U: 4, V: 5, W: 6, X: 7, Y: 8, Z: 9 };
  var VIN_GEWICHT = [8, 7, 6, 5, 4, 3, 2, 10, 0, 9, 8, 7, 6, 5, 4, 3, 2];
  function vinPruefziffer(v) {
    if (!/^[1-5]/.test(v)) return null; // außerhalb Nordamerikas nicht verbindlich
    var sum = 0;
    for (var i = 0; i < 17; i++) {
      var c = v.charAt(i);
      var w = /\d/.test(c) ? parseInt(c, 10) : VIN_WERT[c];
      if (w == null) return null;
      sum += w * VIN_GEWICHT[i];
    }
    var soll = sum % 11; soll = soll === 10 ? 'X' : String(soll);
    return soll === v.charAt(8);
  }
  /* In einer VIN gibt es kein I, O, Q. Wer sie tippt, meint 1 bzw. 0. */
  function vinNormal(value) {
    return (value || '').toUpperCase().replace(/[OQ]/g, '0').replace(/I/g, '1').replace(/[^A-HJ-NPR-Z0-9]/g, '');
  }
  var AUFBAU_DE = [
    [/pickup/i, 'Pickup'], [/sedan|saloon/i, 'Limousine'], [/sport utility|suv/i, 'SUV'],
    [/crossover/i, 'Crossover'], [/convertible|cabrio/i, 'Cabrio'], [/coupe/i, 'Coupé'],
    [/hatchback|liftback/i, 'Schrägheck'], [/wagon/i, 'Kombi'], [/minivan/i, 'Van'], [/van/i, 'Transporter'],
    [/incomplete|chassis/i, 'Fahrgestell'], [/truck/i, 'Lkw']
  ];
  function aufbauDe(s) {
    if (!s) return '';
    for (var i = 0; i < AUFBAU_DE.length; i++) if (AUFBAU_DE[i][0].test(s)) return AUFBAU_DE[i][1];
    return s;
  }
  function psAusHp(hp) { var n = parseFloat(hp); return n ? Math.round(n * 1.01387) : null; }
  /* RAM 1500 ab 2019: zwei Generationen im selben Baujahr — der alte DS als
     „Classic" (Werk Warren) und der neue DT (Werk Sterling Heights). Bremsen,
     Radbolzen und vieles mehr sind verschieden. Das Werk steht in der VIN. */
  function ramGeneration(r) {
    if (!/^ram$/i.test(r.Make || '') || !/^1500/.test(r.Model || '') || parseInt(r.ModelYear, 10) < 2019) return null;
    var werk = (r.PlantCity || '').toUpperCase();
    if (werk.indexOf('WARREN') >= 0) return 'classic';
    if (werk.indexOf('STERLING') >= 0) return 'dt';
    return null;
  }
  /* Fahrzeug → Fahrzeug-Seite im Shop (die sieben Hubs). Titel und Teilezahl
     kommen live aus Liquid (window.USP_HUBS, snippets/usparts-r540-daten). */
  var HUB_REGELN = [
    ['ram-1500-ersatzteile', /^ram$/, /^1500/],
    ['ford-f-150-ersatzteile', /^ford$/, /^f-?150/],
    ['ford-mustang-ersatzteile', /^ford$/, /mustang/],
    ['dodge-challenger-charger-ersatzteile', /^(dodge|chrysler)$/, /challenger|charger|^300/],
    ['chevrolet-camaro-ersatzteile', /^chevrolet$/, /camaro/],
    ['silverado-tahoe-escalade-ersatzteile', /^(chevrolet|cadillac)$/, /silverado|tahoe|suburban|escalade/],
    ['jeep-ersatzteile', /^jeep$/, /wrangler|gladiator|grand cherokee/]
  ];
  function hubFuer(make, model) {
    var mk = nMake(make), md = (model || '').toLowerCase();
    var hubs = window.USP_HUBS || [];
    for (var i = 0; i < HUB_REGELN.length; i++) {
      if (!HUB_REGELN[i][1].test(mk) || !HUB_REGELN[i][2].test(md)) continue;
      for (var j = 0; j < hubs.length; j++) if (hubs[j].h === HUB_REGELN[i][0]) return hubs[j];
    }
    return null;
  }
  window.uspHubFuer = hubFuer;
  window.uspVinPruefziffer = vinPruefziffer;
  window.uspVinNormal = vinNormal;
  function setDetail(scope, key, value) {
    var el = scope.querySelector('[data-usp-detail="' + key + '"]');
    if (!el) return;
    // R540: sichtbare Detailfelder (Kontaktseite „Fahrzeug") nicht überschreiben, wenn von Hand befüllt
    if (el.type !== 'hidden' && el.value && el.value !== el.dataset.uspAuto) return;
    el.value = value || ''; el.dataset.uspAuto = el.value;
  }

  // "Fahrzeug erkannt"-Karte (nur wo ein [data-usp-vin-card] existiert, z. B. Hero).
  function hideVinCard(scope) {
    var card = scope && scope.querySelector && scope.querySelector('[data-usp-vin-card]');
    if (card) { card.hidden = true; card.classList.remove('is-in'); card.innerHTML = ''; }
  }

  function decodeVin(input) {
    var scope = input.closest('[data-usp-vin-scope]') || input.form || document;
    var status = scope.querySelector('[data-usp-vin-status]');
    var raw = vinNormal(input.value);
    // R540: I/O/Q still korrigiert zurückschreiben (nur wenn sich etwas ändert)
    var sichtbar = (input.value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
    var korrigiert = sichtbar !== raw && /[IOQ]/.test(sichtbar) && raw.length === 17;
    if (korrigiert) input.value = raw;

    if (raw.length !== 17 || !VIN_RE.test(raw)) {
      if (status) { status.textContent = ''; status.className = 'usp-vin-status'; }
      if (scope && scope.classList) scope.classList.remove('usp-scanning');
      hideVinCard(scope);
      clearVinData(scope);
      return Promise.resolve();
    }
    if (scope && scope.__uspVeh && scope.__uspVeh.vin !== raw) clearVinData(scope);
    var pz = vinPruefziffer(raw); // true | false | null (nicht verbindlich)

    function show(cls, text) {
      if (!status) return;
      status.className = ('usp-vin-status ' + cls).trim();
      status.textContent = text;
    }

    if (vinCache[raw]) { applyResult(vinCache[raw]); return Promise.resolve(); }
    if (scope && scope.classList) scope.classList.add('usp-scanning');
    show('usp-vin-load', 'Fahrzeug wird gesucht …');

    // Gibt ein Promise zurück, damit der Submit-Guard auf den Decode warten kann
    return fetch('https://vpic.nhtsa.dot.gov/api/vehicles/DecodeVinValues/' + raw + '?format=json')
      .then(function (res) { if (!res.ok) throw new Error('http'); return res.json(); })
      .then(function (data) {
        var r = (data && data.Results && data.Results[0]) || {};
        vinCache[raw] = r;
        applyResult(r);
      })
      .catch(function () {
        if (scope && scope.classList) scope.classList.remove('usp-scanning');
        hideVinCard(scope);
        clearVinData(scope);
        show('usp-vin-err', 'Automatische Erkennung gerade nicht erreichbar. Kein Problem: Schick die Anfrage einfach ab, wir lesen die VIN selbst.');
      });

    function applyResult(r) {
      var make = titleCase(r.Make);
      var model = r.Model || '';
      var year = r.ModelYear || '';
      var engine = buildEngine(r);
      if (!make && !model) {
        if (scope && scope.classList) scope.classList.remove('usp-scanning');
        hideVinCard(scope);
        clearVinData(scope);
        show('usp-vin-err', pz === false
          ? 'Diese VIN ergibt kein Fahrzeug, und die Prüfziffer (Stelle 9) stimmt nicht. Vergleich sie bitte mit dem Fahrzeugschein, Feld E.'
          : 'Zu dieser VIN finden wir kein Fahrzeug. Schick sie trotzdem ab, wir schauen sie uns selbst an.');
        return;
      }
      var fuel = FUEL_DE[r.FuelTypePrimary] || r.FuelTypePrimary || '';
      var ps = psAusHp(r.EngineHP);
      var hp = r.EngineHP ? (Math.round(parseFloat(r.EngineHP)) + ' hp / ' + ps + ' PS') : ''; // Mail an Marcel: beides
      var gen = ramGeneration(r);
      var modellName = (make + ' ' + model).replace(/\s+/g, ' ').trim() + (gen === 'classic' ? ' Classic' : gen === 'dt' ? ' DT' : '');
      var shortLabel = modellName + (year ? ' (' + year + ')' : '');

      // Visible fields — fill only if empty (don't clobber manual input)
      setField(scope, 'brand', make);
      setField(scope, 'model', model);
      setField(scope, 'year', year);
      setField(scope, 'engine', engine);

      // Hidden detail fields → full breakdown into the email (overwrite)
      setDetail(scope, 'vehicle', shortLabel);
      setDetail(scope, 'engine', engine); // Motorisierung (Hubraum · Zyl. · Kraftstoff) — auch auf Hero/Modal ohne sichtbares Motor-Feld
      setDetail(scope, 'bodyclass', aufbauDe(r.BodyClass) + (r.BodyClass && aufbauDe(r.BodyClass) !== r.BodyClass ? ' (' + r.BodyClass + ')' : ''));
      setDetail(scope, 'drive', r.DriveType);
      setDetail(scope, 'hp', hp);
      setDetail(scope, 'transmission', r.TransmissionStyle);
      setDetail(scope, 'fuel', fuel);
      setDetail(scope, 'trim', r.Trim || r.Series2);
      setDetail(scope, 'series', r.Series);
      setDetail(scope, 'manufacturer', r.Manufacturer);
      setDetail(scope, 'plant', [r.PlantCity, r.PlantState, r.PlantCountry].filter(Boolean).join(', '));
      setDetail(scope, 'doors', r.Doors);
      setDetail(scope, 'vehicletype', r.VehicleType);

      // Remember for "save as my vehicle"
      scope.__uspVeh = {
        make: make, model: model, year: year ? parseInt(year, 10) : null, gen: gen,
        disp: r.DisplacementL ? parseFloat(r.DisplacementL) : null,
        cyl: r.EngineCylinders ? parseInt(r.EngineCylinders, 10) : null,
        label: shortLabel, vin: raw
      };
      ensureSaveBtn(scope, status);

      var chip = [shortLabel, aufbauDe(r.BodyClass), engine, shortDrive(r.DriveType)].filter(Boolean).join(' · ');
      var pzHinweis = pz === false
        ? 'Prüfziffer (Stelle 9) stimmt nicht — bitte VIN mit dem Fahrzeugschein, Feld E, vergleichen. Häufig verwechselt: 0/D, 8/B, 5/S.'
        : (korrigiert ? 'In einer VIN gibt es kein I, O oder Q — wir haben 1 bzw. 0 gelesen.' : '');
      setDetail(scope, 'pruefziffer', pz === false ? 'FALSCH – VIN vermutlich vertippt' : (pz === true ? 'stimmt' : ''));
      if (scope && scope.classList) scope.classList.remove('usp-scanning');

      // Wenn ein Karten-Container da ist (Hero): schönes "Fahrzeug erkannt"-Panel
      // statt der reinen Text-Statuszeile. Sonst wie bisher die Statuszeile.
      var card = scope.querySelector && scope.querySelector('[data-usp-vin-card]');
      if (card) {
        // Diagnose-Report: Status-LED + Spec-Zeilen (Terminal-Look, Zeile für Zeile)
        var specPairs = [['Aufbau', aufbauDe(r.BodyClass)], ['Motor', engine], ['Leistung', ps ? ps + ' PS' : ''], ['Antrieb', shortDrive(r.DriveType)], ['Werk', gen ? (gen === 'classic' ? 'Warren · RAM 1500 Classic (DS)' : 'Sterling Heights · neuer RAM 1500 (DT)') : '']];
        var rows = '', ri = 0;
        for (var si = 0; si < specPairs.length; si++) {
          if (!specPairs[si][1]) continue;
          rows += '<span class="usp-vehcard__row" style="--i:' + ri + '">'
            + '<span class="usp-vehcard__k">' + specPairs[si][0] + '</span>'
            + '<span class="usp-vehcard__v">' + esc(specPairs[si][1]) + '</span></span>';
          ri++;
        }
        var hub = hubFuer(make, model);
        card.innerHTML =
          '<div class="usp-vehcard__head">'
          + '<span class="usp-vehcard__led' + (pz === false ? ' is-warn' : '') + '" aria-hidden="true"></span>'
          + '<span class="usp-vehcard__eyebrow">' + (pz === true ? 'Fahrzeug erkannt · Prüfziffer stimmt' : pz === false ? 'Fahrzeug erkannt · Prüfziffer prüfen' : 'Fahrzeug erkannt') + '</span>'
          + '</div>'
          + '<strong class="usp-vehcard__name">' + esc(shortLabel) + '</strong>'
          + (rows ? '<div class="usp-vehcard__specs">' + rows + '</div>' : '')
          + (pzHinweis ? '<p class="usp-vehcard__hinweis">' + esc(pzHinweis) + '</p>' : '')
          + (hub && hub.n ? '<a class="usp-vehcard__hub" href="/collections/' + esc(hub.h) + '">' + hub.n + ' Teile für deinen ' + esc(modellName) + ' liegen schon im Shop →</a>' : '');
        card.classList.toggle('is-warn', pz === false); // R540: Prüfziffer falsch → Karte orange statt grün
        card.hidden = false;
        void card.offsetWidth; // reflow → Reveal-Animation greift
        card.classList.add('is-in');
        if (status) { status.textContent = ''; status.className = 'usp-vin-status'; }
      } else {
        show(pz === false ? 'usp-vin-ok usp-vin-warn' : 'usp-vin-ok', '✓ ' + chip + (pzHinweis ? ' — ' + pzHinweis : ''));
      }
    }
  }

  // Submit-Guard + Aufräumen:
  // 1) Steht eine 17-stellige VIN im Feld, aber der Decode ist noch nicht durch
  //    (Fahrzeug-Detailfeld leer), wird der Submit kurz angehalten, die VIN
  //    dekodiert (max. 4s) und dann automatisch gesendet → Motor & Co. stehen
  //    garantiert in der Mail. Bei Decode-Fehler wird trotzdem gesendet (VIN
  //    selbst ist ja enthalten).
  // 2) Leere versteckte Detailfelder werden deaktiviert → saubere Mails.
  function initFormCleanup() {
    Array.prototype.forEach.call(document.querySelectorAll('form'), function (form) {
      if (!form.querySelector('[data-usp-detail]')) return;
      form.addEventListener('submit', function (e) {
        var vinEl = form.querySelector('[data-usp-vin]');
        if (vinEl && !form.__uspGuardDone) {
          var raw = (vinEl.value || '').toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/g, '');
          var veh = form.querySelector('[data-usp-detail="vehicle"]');
          if (raw.length === 17 && veh && !veh.value) {
            e.preventDefault();
            var fired = false;
            var go = function () {
              if (fired) return;
              fired = true;
              form.__uspGuardDone = true;
              if (form.requestSubmit) { form.requestSubmit(); } else { form.submit(); }
            };
            try { Promise.resolve(decodeVin(vinEl)).then(go, go); } catch (_) { go(); }
            window.setTimeout(go, 4000);
            return;
          }
        }
        form.__uspGuardDone = true;
        Array.prototype.forEach.call(form.querySelectorAll('[data-usp-detail]'), function (el) {
          if (!el.value) el.disabled = true;
        });
      });
    });
  }

  /* ---------- R472 · Anfrage ohne Neuladen -------------------
     Formulare in einem [data-usp-ajax]-Kasten (Hero-Box, Anfrage-Modal)
     gehen per fetch an /contact. Shopify antwortet mit einer Weiterleitung
     auf dieselbe Seite (?contact_posted=true) — das ist der Erfolg. Landet
     die Antwort auf /challenge (Spam-Prüfung) oder ohne contact_posted
     (Fehler), wird klassisch abgeschickt, dann sieht der Kunde Shopifys
     eigene Meldung. Der Gewinn: kein Sprung nach oben, kein Neustart des
     Prüflaufs, und der Erfolg steht genau dort, wo getippt wurde.
     Reihenfolge: initFormCleanup (VIN-Wache) läuft vor diesem Listener und
     ruft bei einer frischen VIN requestSubmit() erneut — erst dann greift
     das hier (form.__uspGuardDone). */
  function zeigeErfolg(form) {
    var box = form.closest('[data-usp-ajax]') || form;
    var done = form.querySelector('[data-usp-done]');
    form.classList.add('is-done');
    if (done) {
      var veh = box.querySelector('[data-usp-detail="vehicle"]');
      var vehTxt = veh && veh.value ? veh.value : '';
      var vehEl = done.querySelector('[data-usp-done-vehicle]');
      if (vehEl) { vehEl.textContent = vehTxt || ''; vehEl.hidden = !vehTxt; }
      done.hidden = false;
      try { done.querySelector('.usp-done__title').setAttribute('tabindex', '-1'); done.querySelector('.usp-done__title').focus({ preventScroll: true }); } catch (_) {}
    }
    var term = box.querySelector('[data-usp-anschluss]');
    var zeile = box.querySelector('[data-usp-anschluss-zeile]');
    if (term) term.classList.add('is-live');
    if (zeile) zeile.textContent = 'Anfrage übermittelt · Vorgang wird angelegt';
    zurDankeSeite(anfrageArt(form), false);
  }

  /* ---------- R642 · Anfrage → Danke-Seite (messbar) ---------------------
     Wachstumsplan 05.10.2026: Die Anfrage löste kein einziges Mess-Ereignis aus —
     Erfolg auf derselben Seite, kein Adresswechsel. Jetzt landet JEDE erfolgreiche
     Anfrage auf /pages/anfrage-danke?art=…, und nur dort wird gemeldet
     (assets/usp-anfrage-messung.js, mit Einwilligung, einmal je Anfrage).
     Anfrage = Formular an /contact MIT contact[Anfrageart]. Kontaktseite und
     Newsletter (UspFussNews) haben das Feld nicht und bleiben, wie sie sind.
     Zwei Wege zum Erfolg, beide hierher:
       1. Hintergrund (initAjaxContact) → zeigeErfolg → zurDankeSeite
       2. klassisch (Anfrageseite, Captcha-Rückfall) → Shopify lädt die Seite mit
          ?contact_posted=true#<Formular-ID> neu → initDankeNachNativ
     Der Merkzettel (sessionStorage, ohne Kontaktdaten) sagt der Danke-Seite, dass
     wirklich gerade etwas rausging. Das Herkunft-Feld (usparts-herkunft) bleibt
     unberührt: Es wird beim Absenden gefüllt, bevor irgendetwas hier passiert. */
  var DANKE_SEITE = '/pages/anfrage-danke';
  var MERKZETTEL = 'usp-anfrage';
  function anfrageArt(form) {
    var f = form && form.querySelector('[name="contact[Anfrageart]"]');
    return f ? (f.value || 'Anfrage') : null;
  }
  function artKurz(art) {
    var a = (art || '').toLowerCase();
    /* „link“ VOR „weiterleitung“: Die Link-Prüfung heißt „Link-Prüfung – Weiterleitung oder Komplettangebot“
       und bekam sonst die Weiterleitungs-Schritte samt „Bestell noch nichts an unsere Adresse“ (Prüfung 06.10.) */
    if (a.indexOf('link') >= 0) return 'link-pruefung';
    if (a.indexOf('weiterleitung') >= 0) return 'weiterleitung';
    if (a.indexOf('oldtimer') >= 0) return 'oldtimer';
    if (a.indexOf('produkt') >= 0) return 'produkt';
    return 'vin';
  }
  function merkzettel(neu) {
    try {
      if (neu === undefined) return JSON.parse(sessionStorage.getItem(MERKZETTEL) || 'null');
      if (neu) sessionStorage.setItem(MERKZETTEL, JSON.stringify(neu)); else sessionStorage.removeItem(MERKZETTEL);
    } catch (_) {}
    return null;
  }
  function initAnfrageMerken() {
    if (document.__uspAnfrageMerken) return;
    document.__uspAnfrageMerken = true;
    document.addEventListener('submit', function (e) {
      var f = e.target;
      if (!f || f.tagName !== 'FORM' || !/\/contact(\b|$|\?|#)/.test(f.getAttribute('action') || '')) return;
      var art = anfrageArt(f);
      if (!art) { merkzettel(null); return; }
      /* Fahrzeug für die Danke-Seite: dekodierte VIN, sonst das frei getippte Fahrzeugfeld (06.10.) */
      var veh = f.querySelector('[data-usp-detail="vehicle"]');
      var frei = f.querySelector('[name="contact[Fahrzeugdaten]"]');
      var fz = veh && veh.value ? veh.value : (frei && frei.value ? frei.value : '');
      merkzettel({
        id: f.id || '', art: art, fz: String(fz).trim().slice(0, 80),
        t: Date.now(), gesendet: false, nr: Date.now().toString(36) + Math.random().toString(36).slice(2, 8)
      });
    }, true);
  }
  function zurDankeSeite(art, ersetzen) {
    if (!art || inEditor || location.pathname === DANKE_SEITE) return;
    var m = merkzettel();
    if (m) { m.gesendet = true; m.t = Date.now(); merkzettel(m); }
    var ziel = DANKE_SEITE + '?art=' + encodeURIComponent(artKurz(art));
    if (ersetzen) location.replace(ziel); else location.assign(ziel);
  }
  function initDankeNachNativ() {
    if (location.search.indexOf('contact_posted=true') === -1) return;
    var m = merkzettel();
    if (!m || m.gesendet || Date.now() - (m.t || 0) > 30 * 60 * 1000) return;
    var anker = location.hash.replace('#', '');
    if (anker && m.id && anker !== m.id) return;
    zurDankeSeite(m.art, true);
  }
  function erfolgZuruecksetzen(form) {
    var box = form.closest('[data-usp-ajax]') || form;
    var done = form.querySelector('[data-usp-done]');
    form.classList.remove('is-done');
    if (done) done.hidden = true;
    form.__uspGuardDone = false;
    Array.prototype.forEach.call(form.querySelectorAll('[data-usp-detail]'), function (el) { el.disabled = false; el.value = ''; });
    Array.prototype.forEach.call(form.querySelectorAll('input[type="text"], input[type="email"], textarea'), function (el) { el.value = ''; });
    var card = box.querySelector('[data-usp-vin-card]'); if (card) { card.hidden = true; card.classList.remove('is-in'); }
    var st = box.querySelector('[data-usp-vin-status]'); if (st) { st.textContent = ''; st.className = 'usp-vin-status'; }
    var z = box.querySelector('[data-usp-vin-zaehler]'); if (z) { z.textContent = '0/17'; z.className = 'usp-vin-zaehler'; }
    var zeile = box.querySelector('[data-usp-anschluss-zeile]'); if (zeile) zeile.textContent = 'VIN eingeben, um den Lauf zu starten';
    var f = form.querySelector('[data-usp-vin]') || form.querySelector('input'); if (f) try { f.focus({ preventScroll: true }); } catch (_) {}
  }
  function initAjaxContact() {
    if (!window.fetch || !window.FormData) return;
    Array.prototype.forEach.call(document.querySelectorAll('[data-usp-ajax] form'), function (form) {
      if (form.__uspAjax) return; form.__uspAjax = true;
      var reset = form.querySelector('[data-usp-done-reset]');
      if (reset) reset.addEventListener('click', function () { erfolgZuruecksetzen(form); });
      form.addEventListener('submit', function (e) {
        if (form.__uspNativ || e.defaultPrevented) return;   // Rückfall bzw. VIN-Wache läuft noch
        /* Die VIN-Wache (initFormCleanup) gibt es nur für Formulare mit
           Detailfeldern — das Weiterleitungsformular hat keine, also gilt es sofort. */
        if (!form.__uspGuardDone && form.querySelector('[data-usp-detail]')) return;
        e.preventDefault();
        /* Gemessen: ein Inline-Skript einer App lauscht am document auf submit
           und ruft form.submit() — vorbei an preventDefault. Es darf das
           Ereignis gar nicht erst sehen. */
        e.stopPropagation();
        var btn = form.querySelector('[data-usp-submit]');
        var lbl = btn && btn.querySelector('[data-usp-submit-label]');
        form.classList.add('is-sending');
        if (btn) btn.disabled = true;
        if (lbl) { lbl.__usp = lbl.textContent; lbl.textContent = 'Wird übertragen …'; }
        var fertig = function (ok) {
          form.classList.remove('is-sending');
          if (btn) btn.disabled = false;
          if (lbl && lbl.__usp) lbl.textContent = lbl.__usp;
          if (ok) { zeigeErfolg(form); return; }
          form.__uspNativ = true;
          if (form.requestSubmit) form.requestSubmit(); else form.submit();
        };
        var action = form.getAttribute('action') || '/contact';
        fetch(action, { method: 'POST', body: new FormData(form), credentials: 'same-origin', redirect: 'follow', headers: { 'Accept': 'text/html' } })
          .then(function (res) {
            var url = res.url || '';
            if (url.indexOf('contact_posted=true') >= 0) return true;
            if (url.indexOf('/challenge') >= 0) return false;
            return res.text().then(function (html) { return html.indexOf('contact_posted=true') >= 0; });
          })
          .then(fertig, function () { fertig(false); });
      });
    });
  }

  function initVinDecoder() {
    var inputs = document.querySelectorAll('[data-usp-vin]');
    if (!inputs.length) return;
    inputs.forEach(function (input) {
      var t = null;
      input.addEventListener('input', function () {
        clearTimeout(t);
        t = setTimeout(function () { decodeVin(input); }, 550);
      });
      input.addEventListener('blur', function () { decodeVin(input); });
      // R540: vorbefüllte VIN (Link mit ?vin=…, Suche, Fußzeile, Browser-Wiederherstellung)
      // sofort erkennen — vorher stand sie nur im Feld, die Fahrzeugkarte kam erst beim Tippen.
      if (vinNormal(input.value).length === 17) decodeVin(input);
    });
  }

  /* ---------- 4. Hero background video --------------------- */
  function initHeroVideo() {
    var vids = document.querySelectorAll('.usp-hero__video');
    Array.prototype.forEach.call(vids, function (v) {
      v.muted = true;
      v.defaultMuted = true;
      v.playsInline = true;
      v.setAttribute('muted', '');
      v.setAttribute('playsinline', '');
      var ready = function () {
        var hero = v.closest('.usp-hero');
        if (hero) hero.classList.add('usp-hero--video-ready');
      };
      var attempt = function () {
        var p = v.play();
        if (p && p.catch) p.catch(function () {/* autoplay blocked → poster stays */});
      };
      if (v.readyState >= 2) { attempt(); ready(); }
      v.addEventListener('loadeddata', function () { attempt(); ready(); }, { once: true });
      v.addEventListener('canplay', attempt, { once: true });
      v.addEventListener('playing', ready, { once: true });
    });
  }

  /* ---------- 5. Count-up numbers -------------------------- */
  function initCounters() {
    var els = document.querySelectorAll('[data-usp-count]');
    if (!els.length) return;
    function run(el) {
      var target = parseFloat(el.getAttribute('data-usp-count')) || 0;
      var prefix = el.getAttribute('data-usp-prefix') || '';
      var suffix = el.getAttribute('data-usp-suffix') || '';
      var decimals = (String(target).split('.')[1] || '').length;
      // Optionales Cockpit-Ziffernblatt im selben .usp-stat — Zeiger + Bogen
      // laufen synchron zum Hochzählen (dekorativ, koppelt an denselben eased-Fortschritt).
      var stat = el.closest ? el.closest('.usp-stat') : null;
      var gauge = stat ? stat.querySelector('[data-usp-gauge]') : null;
      var gArc = gauge ? gauge.querySelector('.usp-gauge__val') : null;
      var gMarker = gauge ? gauge.querySelector('.usp-gauge__marker') : null;
      var gNeedle = gauge ? gauge.querySelector('.usp-gauge__needle') : null;
      var gFrac = gauge ? (parseFloat(gauge.getAttribute('data-usp-gauge-frac')) || 0) : 0;
      var GLEN = 251.33; // Bogenlänge Halbkreis r=80 (π·80)
      function setGauge(cf) {
        if (gArc) gArc.setAttribute('stroke-dashoffset', (GLEN * (1 - cf)).toFixed(2));
        // Marker sitzt IMMER auf dem äußeren Bogen (r=80 um den Hub 100,110) an der
        // aktuellen Füllspitze — kreuzt die zentrale Zahl nie (Text bleibt 100% lesbar).
        if (gMarker) {
          var a = Math.PI * (1 - cf);
          gMarker.setAttribute('cx', (100 + 80 * Math.cos(a)).toFixed(2));
          gMarker.setAttribute('cy', (110 - 80 * Math.sin(a)).toFixed(2));
        }
        // R22: echter Zeiger schwenkt mit — -90° = 0 % (links) bis +90° = 100 % (rechts).
        if (gNeedle) gNeedle.setAttribute('transform', 'rotate(' + (-90 + 180 * cf).toFixed(2) + ' 100 110)');
      }
      if (reduceMotion) { el.textContent = prefix + target.toLocaleString('de-DE') + suffix; setGauge(gFrac); return; }
      var start = null, dur = 1400;
      function frame(ts) {
        if (!start) start = ts;
        var p = Math.min((ts - start) / dur, 1);
        var eased = 1 - Math.pow(1 - p, 3);
        var val = target * eased;
        el.textContent = prefix + val.toLocaleString('de-DE', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }) + suffix;
        setGauge(gFrac * eased);
        if (p < 1) requestAnimationFrame(frame);
        else { el.textContent = prefix + target.toLocaleString('de-DE', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }) + suffix; setGauge(gFrac); }
      }
      requestAnimationFrame(frame);
    }
    if (!('IntersectionObserver' in window)) { Array.prototype.forEach.call(els, run); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) { if (e.isIntersecting) { run(e.target); io.unobserve(e.target); } });
    }, { threshold: 0.4 });
    Array.prototype.forEach.call(els, function (el) { io.observe(el); });
  }

  /* ========================================================
     6. GARAGE ("Mein Fahrzeug") + Fitment-Check  — no app
     Storage: localStorage. Match against custom.fitment_rules_text
     Rule format: "Make, Model, Engine, YYYY-YYYY" (engine empty = any)
     ======================================================== */
  var GKEY = 'usp_garage_vehicle_v1';
  function gGet() { try { return JSON.parse(localStorage.getItem(GKEY) || 'null'); } catch (e) { return null; } }
  function gSet(v) { try { localStorage.setItem(GKEY, JSON.stringify(v)); } catch (e) {} document.dispatchEvent(new CustomEvent('usp:garage')); }
  function gClear() { try { localStorage.removeItem(GKEY); } catch (e) {} document.dispatchEvent(new CustomEvent('usp:garage')); }

  var MAKE_ALIAS = { chevy: 'chevrolet', 'mercedes-benz': 'mercedes', vw: 'volkswagen' };
  function nMake(s) { s = (s || '').toLowerCase().trim().replace(/\./g, ''); return MAKE_ALIAS[s] || s; }
  function nModel(s) { return (s || '').toLowerCase().replace(/[\s\-_/]/g, ''); }

  function engSig(s) {
    s = s || '';
    var d = null, c = null;
    var md = s.match(/(\d(?:\.\d)?)\s*l\b/i); if (md) d = parseFloat(md[1]);
    var mv = s.match(/\bV[\s-]?(\d{1,2})\b/i); var mi = s.match(/\bI[\s-]?(\d)\b/i);
    if (mv) c = parseInt(mv[1], 10); else if (mi) c = parseInt(mi[1], 10);
    return { d: d, c: c };
  }
  function yRange(s) {
    s = s || '';
    var m = s.match(/(\d{4})\s*[-–]\s*(\d{4})/); if (m) return [+m[1], +m[2]];
    m = s.match(/(\d{4})\s*\+/); if (m) return [+m[1], 9999];
    m = s.match(/(\d{4})/); if (m) return [+m[1], +m[1]];
    return null;
  }
  function parseRule(line) {
    var p = line.split(',').map(function (x) { return x.trim(); });
    if (p.length < 2) return null;
    var make = p[0], model = p[1], engine = '', yr = null;
    if (p.length >= 5 && /^\d{4}$/.test(p[2]) && /^\d{4}$/.test(p[3])) {
      yr = [+p[2], +p[3]]; engine = p[4] || '';
    } else {
      engine = p[2] || ''; yr = yRange(p[3] || '');
      if (!yr && /\d{4}/.test(engine)) { yr = yRange(engine); engine = ''; }
    }
    return { make: make, model: model, engine: engine, yr: yr };
  }
  function modelMatch(a, b) {
    if (!a || !b) return false;
    if (a === b) return true;
    return a.length >= 3 && b.length >= 3 && (a.indexOf(b) >= 0 || b.indexOf(a) >= 0);
  }
  function engMatch(ruleEng, veh) {
    if (!ruleEng) return true;            // "Motor egal"
    var rs = engSig(ruleEng);
    if (rs.d == null && rs.c == null) return null;
    var dispOk = (rs.d != null && veh.disp != null) ? Math.abs(rs.d - veh.disp) <= 0.15 : null;
    var cylOk = (rs.c != null && veh.cyl != null) ? rs.c === veh.cyl : null;
    if (dispOk === false || cylOk === false) return false;
    if (dispOk === true || cylOk === true) return true;
    return null;
  }
  /* R540 · RAM 1500, Baujahre 2019–2024: Es gibt ZWEI verschiedene Autos mit
     demselben Namen — den alten DS als „Classic" und den neuen DT. Bremsen,
     Lochkreis (5 gegen 6 Radbolzen), Fahrwerk sind verschieden. Befund 22.09.:
     jeder RAM 2019 bekam „✓ Passt" für Classic-Bremsen, weil nur das Baujahr
     verglichen wurde. Die eigene RAM-Seite sagt: „Das Baujahr allein sagt also
     wenig darüber, welcher RAM vor dir steht." Jetzt:
       Regel nennt „Classic" / „DS"  → nur der Classic passt
       Regel nennt „DT"              → nur der DT passt
       Regel nennt keins             → ab 2019 höchstens „vermutlich"
     Die Generation kennt nur die VIN (Werk Warren = Classic, Sterling Heights
     = DT). Ohne VIN bleibt es bei „vermutlich" — ehrlich statt grün. */
  function ramDoppelt(rule, veh) {
    if (veh.makeN !== 'ram' || !/^1500/.test(veh.modelN) || !veh.year || veh.year < 2019 || veh.year > 2024) return null;
    var t = (rule.model + ' ' + rule.engine).toLowerCase();
    var nurClassic = /classic|\bds\b/.test(t), nurDt = /\bdt\b/.test(t);
    if (nurClassic && !nurDt) return veh.gen === 'classic' ? 'ok' : veh.gen === 'dt' ? 'none' : 'maybe';
    if (nurDt && !nurClassic) return veh.gen === 'dt' ? 'ok' : veh.gen === 'classic' ? 'none' : 'maybe';
    return 'maybe';
  }
  function matchOne(rule, veh) {
    if (nMake(rule.make) !== veh.makeN) return 'none';
    if (!modelMatch(nModel(rule.model), veh.modelN)) return 'none';
    if (rule.yr && veh.year) { if (veh.year < rule.yr[0] || veh.year > rule.yr[1]) return 'none'; }
    var gen = ramDoppelt(rule, veh);
    if (gen === 'none') return 'none';
    var e = engMatch(rule.engine, veh);
    if (e === true && gen !== 'maybe') return (rule.yr && !veh.year) ? 'maybe' : 'fit';
    return 'maybe'; // engine unknown or mismatch → cautious, never hard-no
  }
  function matchFitment(text, veh) {
    var lines = (text || '').split(/\r?\n/).map(function (x) { return x.trim(); }).filter(Boolean);
    var best = 'none';
    for (var i = 0; i < lines.length; i++) {
      var r = parseRule(lines[i]); if (!r) continue;
      var m = matchOne(r, veh);
      if (m === 'fit') return 'fit';
      if (m === 'maybe') best = 'maybe';
    }
    return best;
  }
  function vehNorm(o) {
    return {
      make: o.make, model: o.model, year: o.year ? parseInt(o.year, 10) : null,
      disp: o.disp != null ? parseFloat(o.disp) : null, cyl: o.cyl != null ? parseInt(o.cyl, 10) : null,
      engine: o.engine || '', // R21: Motor-Label (z. B. "3.6L V6") für Chip-Anzeige
      label: o.label || ((o.make || '') + ' ' + (o.model || '') + (o.year ? ' (' + o.year + ')' : '')).trim(),
      vin: o.vin || '', makeN: nMake(o.make), modelN: nModel(o.model),
      gen: o.gen || null // R540: 'classic' | 'dt' | null (RAM 1500 ab 2019, aus dem Werk in der VIN)
    };
  }

  // "Als mein Fahrzeug speichern" button after a successful decode
  function ensureSaveBtn(scope, status) {
    if (!scope || scope.querySelector('.usp-garage-save')) return;
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'usp-garage-save';
    btn.textContent = 'Fahrzeug merken';
    btn.title = 'Wird in diesem Browser gespeichert – damit wir dir auf Produktseiten direkt zeigen, ob ein Teil passt.';
    btn.addEventListener('click', function () {
      if (!scope.__uspVeh) return;
      gSet(vehNorm(scope.__uspVeh));
      btn.textContent = '✓ Gemerkt – in diesem Browser gespeichert';
      btn.classList.add('is-saved');
    });
    // R540: nach der Fahrzeugkarte, nicht zwischen VIN-Feld und Karte (dort schob er die Karte weg)
    var karte = scope.querySelector('[data-usp-vin-card]');
    if (karte && karte.parentNode) karte.parentNode.insertBefore(btn, karte.nextSibling);
    else if (status && status.parentNode) status.parentNode.insertBefore(btn, status.nextSibling);
    else scope.appendChild(btn);
  }

  // Render the garage chip(s) + PDP fit badge
  function renderGarage() {
    var veh = gGet();

    Array.prototype.forEach.call(document.querySelectorAll('[data-usp-garage-chip]'), function (chip) {
      if (veh) {
        // R21: Diagnose-Zeile statt Emoji-Chip — grüne LED + Mono-Label (+ Motor,
        // falls gewählt), Aktionen rechts als kompakte Ghost-Buttons.
        chip.innerHTML = '<span class="usp-gchip"><span class="usp-gchip__led" aria-hidden="true"></span>'
          + '<span class="usp-gchip__k">Fahrzeug</span>'
          + '<span class="usp-gchip__txt"><strong></strong><em class="usp-gchip__eng" hidden></em></span>'
          + '<span class="usp-gchip__actions">'
          + '<button type="button" class="usp-gchip__edit" data-usp-garage-open>Ändern</button>'
          + '<button type="button" class="usp-gchip__x" data-usp-garage-clear aria-label="Fahrzeug entfernen" title="Fahrzeug entfernen">✕</button>'
          + '</span></span>';
        chip.querySelector('strong').textContent = veh.label || (veh.make + ' ' + veh.model);
        if (veh.engine) {
          var ee = chip.querySelector('.usp-gchip__eng');
          ee.textContent = ' · ' + veh.engine; ee.hidden = false;
        }
        chip.hidden = false;
      } else {
        // No vehicle yet → keep the chip empty/hidden; the fit-result box is the single prompt
        chip.innerHTML = '';
        chip.hidden = true;
      }
    });

    Array.prototype.forEach.call(document.querySelectorAll('[data-usp-fitment-result]'), function (box) {
      var compact = box.classList.contains('usp-atc-fit');
      var sec = box.closest('.shopify-section') || document;
      var rulesEl = sec.querySelector('[data-usp-fitment-rules]') || document.querySelector('[data-usp-fitment-rules]');
      var rules = rulesEl ? (rulesEl.textContent || '') : '';
      function cta(label) {
        return compact
          ? '<button type="button" class="usp-fitresult__link" data-usp-request-open>' + label + ' →</button>'
          : '<button type="button" class="usp-btn usp-btn--primary usp-fitresult__cta" data-usp-request-open>' + label + '</button>';
      }
      function set(cls, html) { box.className = 'usp-fitresult ' + cls + (compact ? ' usp-atc-fit' : ''); box.innerHTML = html; }

      if (!veh) {
        set('usp-fitresult--neutral', '<strong>Dein Fahrzeug?</strong> Speichere es für den automatischen Passt-Check. '
          + '<button type="button" class="usp-fitresult__link" data-usp-garage-open>Fahrzeug wählen →</button>');
        return;
      }
      var L = esc(veh.label);
      var state = rules.trim() ? matchFitment(rules, veh) : 'nodata';
      if (state === 'fit') {
        set('usp-fitresult--fit', '✓ <strong>Passt zu deinem ' + L + '</strong>');
      } else if (state === 'maybe') {
        set('usp-fitresult--maybe', '≈ Passt vermutlich zu deinem <strong>' + L + '</strong> – Variante kurz prüfen. ' + cta('Jetzt prüfen lassen'));
      } else if (state === 'none') {
        /* R540 · „nicht gelistet" ehrlich sagen statt „noch nicht bestätigt"
           (das klang nach „könnte passen" und erzeugte sinnlose Anfragen).
           Weiter neutral, nie rot — und mit Weg zu den Teilen, die passen. */
        var hubN = window.uspHubFuer ? window.uspHubFuer(veh.make, veh.model) : null;
        set('usp-fitresult--neutral', 'Dein <strong>' + L + '</strong> steht nicht in der Passliste dieses Teils. '
          + (hubN ? '<a class="usp-fitresult__link" href="/collections/' + esc(hubN.h) + '">Teile für deinen ' + esc(veh.make + ' ' + veh.model) + ' →</a> ' : '')
          + cta('Trotzdem prüfen lassen'));
      } else { // nodata → NEUTRAL, never red, with strong request CTA
        set('usp-fitresult--neutral', 'Für <strong>' + L + '</strong> noch nicht bestätigt – wir prüfen es kurz für dich. ' + cta('Teile per VIN anfragen'));
      }
    });

    renderCardFitment(veh);
  }

  // Produktkarten-Fitment-Chip: rendert NUR bei gemerktem Fahrzeug UND
  // Regel-Treffer (fit/maybe) → keine Grid-Unruhe ohne Daten/ohne Fahrzeug.
  function renderCardFitment(veh) {
    Array.prototype.forEach.call(document.querySelectorAll('[data-usp-card-fit]'), function (box) {
      var rules = box.getAttribute('data-usp-card-rules') || '';
      if (!veh || !rules.trim()) { box.hidden = true; box.innerHTML = ''; return; }
      var state = matchFitment(rules, veh);
      if (state === 'fit') {
        box.innerHTML = '<span class="usp-fit usp-fit--ok"><span class="usp-fit__led" aria-hidden="true"></span>PASST</span>';
        box.hidden = false;
      } else if (state === 'maybe') {
        box.innerHTML = '<span class="usp-fit usp-fit--maybe"><span class="usp-fit__led" aria-hidden="true"></span>PRÜFEN</span>';
        box.hidden = false;
      } else { box.hidden = true; box.innerHTML = ''; }
    });
  }
  function esc(s) { return (s || '').replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }

  // Manual cascading picker (Make → Model → Year → Engine) from window.USP_VEHICLES.
  // R21: Motor als 4. Stufe (OPTIONAL, „Weiß ich nicht") — ein bekannter Motor macht
  // den Fitment-Check präzise (PASST statt PRÜFEN), ohne ihn kommt man trotzdem durch.
  function openPicker() {
    var data = window.USP_VEHICLES || [];
    var overlay = document.createElement('div');
    overlay.className = 'usp-picker';
    overlay.innerHTML =
      '<div class="usp-picker__box usp-hud" role="dialog" aria-modal="true" aria-label="Fahrzeug wählen">'
      + '<button type="button" class="usp-picker__close" aria-label="Schließen">✕</button>'
      + '<div class="usp-term__head"><span class="usp-term__led" aria-hidden="true"></span><span class="usp-term__title">Diagnose · Fahrzeugwahl</span></div>'
      + '<h3 class="usp-picker__title">Mein Fahrzeug wählen</h3>'
      + '<label class="usp-label">Marke</label><select class="usp-select" data-p="make"></select>'
      + '<label class="usp-label">Modell</label><select class="usp-select" data-p="model" disabled></select>'
      + '<label class="usp-label">Baujahr</label><select class="usp-select" data-p="year" disabled></select>'
      + '<label class="usp-label">Motor <span class="usp-picker__opt">(optional)</span></label><select class="usp-select" data-p="engine" disabled></select>'
      + '<button type="button" class="usp-btn usp-btn--primary usp-btn--block usp-picker__save" disabled>Fahrzeug merken</button>'
      + '<p class="usp-note" style="text-align:center">Wird in diesem Browser gespeichert – kein Konto nötig.</p>'
      + (data.length ? '' : '<p class="usp-note">Fahrzeugliste konnte nicht geladen werden – du kannst trotzdem per VIN anfragen.</p>')
      + '</div>';
    document.body.appendChild(overlay);
    var mk = overlay.querySelector('[data-p="make"]'),
        md = overlay.querySelector('[data-p="model"]'),
        yr = overlay.querySelector('[data-p="year"]'),
        eng = overlay.querySelector('[data-p="engine"]'),
        save = overlay.querySelector('.usp-picker__save');

    var makes = Array.from(new Set(data.map(function (r) { return r[0]; }))).sort();
    mk.innerHTML = '<option value="">Bitte wählen</option>' + makes.map(function (m) { return '<option>' + esc(m) + '</option>'; }).join('');

    function resetEng() { eng.disabled = true; eng.innerHTML = ''; }
    mk.addEventListener('change', function () {
      var models = Array.from(new Set(data.filter(function (r) { return r[0] === mk.value; }).map(function (r) { return r[1]; }))).sort();
      md.innerHTML = '<option value="">Bitte wählen</option>' + models.map(function (m) { return '<option>' + esc(m) + '</option>'; }).join('');
      md.disabled = !mk.value; yr.disabled = true; yr.innerHTML = ''; resetEng(); save.disabled = true;
    });
    md.addEventListener('change', function () {
      var rows = data.filter(function (r) { return r[0] === mk.value && r[1] === md.value; });
      var min = Math.min.apply(null, rows.map(function (r) { return r[2]; }));
      var max = Math.max.apply(null, rows.map(function (r) { return r[3]; }));
      var years = []; for (var y = max; y >= min; y--) years.push(y);
      yr.innerHTML = '<option value="">Bitte wählen</option>' + years.map(function (y) { return '<option>' + y + '</option>'; }).join('');
      yr.disabled = !md.value; resetEng(); save.disabled = true;
    });
    yr.addEventListener('change', function () {
      save.disabled = !yr.value;
      // Motoren, deren Bauzeitraum das gewählte Jahr enthält
      var y = parseInt(yr.value, 10);
      var engines = Array.from(new Set(data.filter(function (r) {
        return r[0] === mk.value && r[1] === md.value && r[2] <= y && y <= r[3] && r[4];
      }).map(function (r) { return r[4]; }))).sort();
      if (yr.value && engines.length) {
        eng.innerHTML = '<option value="">Weiß ich nicht / egal</option>' + engines.map(function (e) { return '<option>' + esc(e) + '</option>'; }).join('');
        eng.disabled = false;
      } else { resetEng(); }
    });
    save.addEventListener('click', function () {
      var sig = eng.value ? engSig(eng.value) : { d: null, c: null };
      gSet(vehNorm({
        make: mk.value, model: md.value, year: yr.value,
        engine: eng.value, disp: sig.d, cyl: sig.c,
        label: mk.value + ' ' + md.value + ' (' + yr.value + ')'
      }));
      close();
    });
    function close() { overlay.remove(); }
    overlay.addEventListener('click', function (e) { if (e.target === overlay) close(); });
    overlay.querySelector('.usp-picker__close').addEventListener('click', close);
  }

  function initGarage() {
    renderGarage();
    document.addEventListener('click', function (e) {
      var open = e.target.closest && e.target.closest('[data-usp-garage-open]');
      var clr = e.target.closest && e.target.closest('[data-usp-garage-clear]');
      if (open) { e.preventDefault(); openPicker(); }
      if (clr) { e.preventDefault(); gClear(); }
    });
  }
  document.addEventListener('usp:garage', renderGarage);

  /* ---------- 7. Per-product request modal --------------- */
  function getModal() { return document.querySelector('[data-usp-request-modal]'); }
  function setVal(scope, sel, val) { var el = scope.querySelector(sel); if (el && val != null) el.value = val; }
  function openRequestModal(teil, trigger) {
    var m = getModal(); if (!m) return;
    m.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    // Quelle präzisieren: Basis (Seite, aus Liquid) + Auslöser (Button-Kontext)
    var src = m.querySelector('[data-usp-source]');
    if (src) {
      if (!src.__uspBase) src.__uspBase = src.value;
      src.value = trigger ? (src.__uspBase + ' · Auslöser: ' + trigger) : src.__uspBase;
    }
    var p = window.USP_PRODUCT;
    var prodLine = m.querySelector('[data-usp-modal-product]');
    if (p) {
      setVal(m, '[data-usp-product]', p.title);
      setVal(m, '[data-usp-product-url]', p.url);
      setVal(m, '[data-usp-sku]', p.sku);
      var part = m.querySelector('[data-usp-part]'); if (part && !part.value) part.value = teil || p.title;
      if (prodLine) { prodLine.textContent = p.title; prodLine.hidden = false; }
    } else if (teil) {
      var part2 = m.querySelector('[data-usp-part]'); if (part2 && !part2.value) part2.value = teil;
    }
    var veh = gGet();
    var vin = m.querySelector('[data-usp-vin]');
    if (vin && veh && veh.vin && !vin.value) { vin.value = veh.vin; decodeVin(vin); }
    var f = m.querySelector('input, textarea'); if (f) try { f.focus(); } catch (e) {}
  }
  function closeRequestModal() { var m = getModal(); if (m) { m.hidden = true; document.documentElement.style.overflow = ''; } }
  function initRequestModal() {
    var m = getModal();
    var postedHere = false; // nur wieder öffnen, wenn DIESES Formular gepostet hat (URL-Anker)
    try { postedHere = !!(m && location.hash && m.querySelector(location.hash)); } catch (_) {}
    if (m && m.querySelector('[data-usp-modal-success]') && postedHere) openRequestModal();
    if (document.__uspModalBound) return; document.__uspModalBound = true;
    /* R540: Auf der Anfrageseite selbst steht das Formular schon — dort öffnet kein
       ZWEITES Formular im Fenster, sondern die Seite springt zum Formular (Befund A9). */
    function formHier() {
      var f = document.querySelector('main .usp-reqform');
      return f && !f.closest('[data-usp-request-modal]') ? f : null;
    }
    function zumFormular(f, teil) {
      f.scrollIntoView({ behavior: 'smooth', block: 'start' });
      if (teil) { var t = f.querySelector('[id$="-part"]'); if (t && !t.value) t.value = teil; }
      var v = f.querySelector('[data-usp-vin]'); if (v) try { v.focus({ preventScroll: true }); } catch (_) {}
    }
    document.addEventListener('click', function (e) {
      var openBtn = e.target.closest && e.target.closest('[data-usp-request-open]');
      var anfrageLink = e.target.closest && e.target.closest('a[href*="/pages/anfrage"], a[href*="/pages/us-autoteile-teileanfrage"]');
      var hier = (openBtn || anfrageLink) ? formHier() : null;
      if (hier && !(anfrageLink && anfrageLink.closest('.usp-reqform'))) {
        e.preventDefault();
        var tl = null; try { tl = anfrageLink ? new URL(anfrageLink.href, location.origin).searchParams.get('teil') : null; } catch (_) {}
        zumFormular(hier, tl); return;
      }
      /* 29.09.2026 — Regel der Leitung: „Knöpfe (.usp-btn / .usp-fitment__cta-btn / Marker) öffnen das Fenster, Textlinks und Kacheln führen zur Seite, Kopf- und Fußgruppe navigieren immer.“
         Anlass, Marcel: „Teile per VIN anfragen in Menü oder Footer Menü können gerne auf die Anfrageseite führen. Sonst
         erreicht man die nirgends." Bis dahin öffnete JEDER Link auf die Anfrageseite das Fenster.
           · Kopf- und Fußgruppe (Ankündigung, Kopf-Knopf, Mega-Menü, Handy-Schublade, Fußzeile): immer auf die Seite,
             auch der Schubladen-Knopf mit data-usp-request-open.
           · Knopf-Optik im Code: .usp-btn oder eine Klasse mit „cta“ (z. B. .usp-fitment__cta-btn, .usp-cart-anfrage__cta);
             auf der Produktseite öffnet jeder Anfrage-Link das Fenster (es schickt Produkt und SKU mit).
         Dieselbe Kopf-/Fuß-Ausnahme steht in usp-r560.js; das Oldtimer-Zweckfenster bleibt, wie es ist. */
      if (anfrageLink && anfrageLink.closest('.shopify-section-group-header-group, .shopify-section-group-footer-group')) return;
      if (openBtn) {
        e.preventDefault();
        var trig =
          openBtn.closest('.usp-tabbar') ? 'Tab-Bar (mobil)'
          : (openBtn.closest('.usp-reqfab') || openBtn.classList.contains('usp-reqfab')) ? 'Schwebe-Button'
          : openBtn.closest('.usp-atc-extra') ? 'Produktseite – Kaufbox'
          : openBtn.closest('.usp-fitresult') ? 'Produktseite – Fitment-Check'
          : (openBtn.closest('.usp-drawer-cta') || openBtn.classList.contains('usp-drawer-cta')) ? 'Menü-Drawer'
          : 'Button';
        openRequestModal(null, trig);
        return;
      }
      if (anfrageLink && (window.USP_PRODUCT || anfrageLink.matches('.usp-btn, [class*="cta"]'))) {
        e.preventDefault();
        var teil = null; try { teil = new URL(anfrageLink.href, location.origin).searchParams.get('teil'); } catch (_) {}
        openRequestModal(teil, 'Anfrage-Link'); return;
      }
      if (e.target.closest && e.target.closest('[data-usp-request-close]')) { e.preventDefault(); closeRequestModal(); return; }
      if (e.target.hasAttribute && e.target.hasAttribute('data-usp-request-modal')) { closeRequestModal(); }
    });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') closeRequestModal(); });
  }

  /* ---------- 9. Mobile drawer "VIN / Teil anfragen" CTA ---- */
  function injectDrawerCta() {
    var container = document.querySelector('.menu-list--mobile .menu-list__scroll-container');
    if (!container || container.querySelector('[data-usp-drawer-cta]')) return;
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'usp-btn usp-btn--primary usp-drawer-cta';
    btn.setAttribute('data-usp-drawer-cta', '');
    btn.setAttribute('data-usp-request-open', '');
    btn.textContent = 'Teile per VIN anfragen';
    container.insertBefore(btn, container.firstChild);
  }

  /* ---------- 8. Inject CTA + fit badge next to Add-to-Cart */
  function injectAtc() {
    if (!window.USP_PRODUCT) return;            // product pages only
    if (document.querySelector('[data-usp-atc-injected]')) return;
    var atc = document.querySelector(
      'product-form button[name="add"], form[action*="/cart/add"] [type="submit"], button[name="add"], .product-form__submit, .add-to-cart, [data-add-to-cart]'
    );
    if (!atc) return;
    var host = atc.closest('product-form, .product-form, form') || atc;
    var anfrage = '/pages/us-autoteile-teileanfrage?teil=' + encodeURIComponent(window.USP_PRODUCT.title || '');
    var wrap = document.createElement('div');
    wrap.className = 'usp-atc-extra';
    wrap.setAttribute('data-usp-atc-injected', '');
    wrap.innerHTML =
      '<div class="usp-atc-chip" data-usp-garage-chip hidden></div>'
      + '<div class="usp-atc-fit" data-usp-fitment-result data-usp-anfrage="' + esc(anfrage) + '"></div>'
      + '<button type="button" class="usp-btn usp-atc-ask" data-usp-request-open>Jetzt direkt anfragen</button>';
    if (host.parentNode) host.parentNode.insertBefore(wrap, host.nextSibling);
    renderGarage();
  }

  /* ---------- Fahrzeug-Box im Hero (card_mode: vehicle) -------
     Marke → Modell → Motor aus window.USP_VEHICLES; "Teile anzeigen" setzt die
     Garage (gSet → usp:garage), der Finder darunter filtert automatisch.
     Das VIN-Feld nutzt den vorhandenen Decoder (data-usp-vin). */
  function initVehicleBoxes() {
    var boxes = document.querySelectorAll('[data-usp-vehiclebox]');
    if (!boxes.length) return;
    var data = window.USP_VEHICLES || [];
    Array.prototype.forEach.call(boxes, function (box) {
      if (box.__uspVehInit) return; box.__uspVehInit = true;
      var mk = box.querySelector('[data-usp-veh="make"]'),
          md = box.querySelector('[data-usp-veh="model"]'),
          yr = box.querySelector('[data-usp-veh="year"]'), // R21: Baujahr-Stufe (optional im Markup alter Instanzen)
          en = box.querySelector('[data-usp-veh="engine"]'),
          go = box.querySelector('[data-usp-veh-go]');
      if (!mk || !md || !en) return;
      var seen = {}, makes = [];
      data.forEach(function (r) { if (r[0] && !seen[r[0]]) { seen[r[0]] = 1; makes.push(r[0]); } });
      makes.sort();
      mk.innerHTML = '<option value="">Marke</option>' + makes.map(function (m) { return '<option>' + esc(m) + '</option>'; }).join('');
      function resetEngine() { en.disabled = true; en.innerHTML = '<option value="">Motor (optional)</option>'; }
      function fillModels() {
        var s = {}, models = [];
        data.forEach(function (r) { if (r[0] === mk.value && r[1] && !s[r[1]]) { s[r[1]] = 1; models.push(r[1]); } });
        models.sort();
        md.innerHTML = '<option value="">Modell</option>' + models.map(function (m) { return '<option>' + esc(m) + '</option>'; }).join('');
        md.disabled = !mk.value;
        if (yr) { yr.disabled = true; yr.innerHTML = '<option value="">Baujahr</option>'; }
        resetEngine();
      }
      function fillYears() {
        if (!yr) { fillEngines(); return; }
        var rows = data.filter(function (r) { return r[0] === mk.value && r[1] === md.value; });
        if (!rows.length) { yr.disabled = true; return; }
        var min = Math.min.apply(null, rows.map(function (r) { return r[2]; }));
        var max = Math.max.apply(null, rows.map(function (r) { return r[3]; }));
        var opts = []; for (var y = max; y >= min; y--) opts.push(y);
        yr.innerHTML = '<option value="">Baujahr</option>' + opts.map(function (y) { return '<option>' + y + '</option>'; }).join('');
        yr.disabled = !md.value;
        resetEngine();
      }
      function fillEngines() {
        var y = yr && yr.value ? parseInt(yr.value, 10) : null;
        var s = {}, engs = [];
        data.forEach(function (r) {
          if (r[0] !== mk.value || r[1] !== md.value || !r[4]) return;
          if (y != null && !(r[2] <= y && y <= r[3])) return; // jahresgefiltert
          if (!s[r[4]]) { s[r[4]] = 1; engs.push(r[4]); }
        });
        engs.sort();
        en.innerHTML = '<option value="">Motor (optional)</option>' + engs.map(function (e) { return '<option>' + esc(e) + '</option>'; }).join('');
        en.disabled = !md.value;
      }
      mk.addEventListener('change', fillModels);
      md.addEventListener('change', fillYears);
      if (yr) yr.addEventListener('change', fillEngines);
      var cur = gGet();
      if (cur && cur.make) {
        mk.value = cur.make;
        if (mk.value === cur.make) {
          fillModels();
          if (cur.model) {
            md.value = cur.model;
            if (md.value === cur.model) {
              fillYears();
              if (yr && cur.year) { yr.value = String(cur.year); }
              fillEngines();
              if (cur.engine) en.value = cur.engine;
            }
          }
        }
      }
      if (go) go.addEventListener('click', function () {
        var missingYear = yr && !yr.value;
        if (!mk.value || !md.value || missingYear) {
          var st = box.querySelector('[data-usp-vin-status]');
          if (st) st.textContent = missingYear ? 'Bitte auch das Baujahr wählen.' : 'Bitte Marke und Modell wählen.';
          return;
        }
        var sig = engSig(en.value || '');
        gSet(vehNorm({ make: mk.value, model: md.value, year: yr ? yr.value : null,
          engine: en.value || '', disp: sig.d, cyl: sig.c,
          label: (mk.value + ' ' + md.value + (yr && yr.value ? ' (' + yr.value + ')' : '')).trim() }));
        var target = document.querySelector('.toolbar-wrap') || document.querySelector('[data-myveh]');
        if (target) try { target.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (e) {}
      });
    });
  }

  /* ---------- Posted-Toast: Feedback nach Formular-Post ohne sichtbare Erfolgsmeldung */
  function initPostedToast() {
    if (location.search.indexOf('contact_posted=true') === -1) return;
    var visible = false;
    document.querySelectorAll('.usp-success').forEach(function (e) { if (e.offsetParent !== null) visible = true; });
    if (visible) return; // Inline-Meldung oder offenes Modal übernimmt
    var t = document.createElement('div');
    t.className = 'usp-toast';
    t.setAttribute('role', 'status');
    t.innerHTML = '<span class="usp-toast__led" aria-hidden="true"></span> Anfrage gesendet – wir melden uns mit deinem Angebot.';
    document.body.appendChild(t);
    setTimeout(function () { t.classList.add('is-out'); }, 6000);
    setTimeout(function () { if (t.parentNode) t.parentNode.removeChild(t); }, 6600);
  }

  /* ---------- init ---------------------------------------- */
  function init() { initAnfrageMerken(); initDankeNachNativ(); initReveals(); initScans(); initBootSeq(); initGlow(); initVinDecoder(); initHeroVideo(); initFormCleanup(); initAjaxContact(); initCounters(); initGarage(); initVehicleBoxes(); initRequestModal(); injectAtc(); injectDrawerCta(); initPostedToast(); }
  // Drawer can be lazy-rendered → retry when the mobile menu is opened
  document.addEventListener('click', function (e) {
    if (e.target.closest && e.target.closest('.header__icon--menu, .menu-drawer-container summary')) {
      setTimeout(injectDrawerCta, 60);
    }
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // Re-run when sections are re-rendered in the theme editor
  document.addEventListener('shopify:section:load', function () { initScans(); initBootSeq(); initGlow(); initVinDecoder(); initHeroVideo(); initFormCleanup(); initAjaxContact(); initCounters(); renderGarage(); initVehicleBoxes(); initRequestModal(); injectAtc(); injectDrawerCta(); });
})();
